import { getCompatibleAuthTokenHttp } from 'src/services/utils/enhancedAuthTokenHttp';

export const GUIDELINE_BUDDY_VENDOR = 'GuidelineBuddy' as const;

export type GuidelineBuddyVendor = typeof GUIDELINE_BUDDY_VENDOR;

export type AiChatMode =
  | 'auto'
  | 'full_snapshot'
  | 'incremental_update'
  | 'query_only';

export interface SendGuidelineBuddyMessageParams {
  loanId: string;
  message: string;
  mode?: AiChatMode;
  force?: boolean;
}

export interface SendGuidelineBuddyMessageResult {
  loanId: string;
  vendor: GuidelineBuddyVendor;
  sessionId: string;
  createdSession: boolean;
  payloadMode: AiChatMode;
  snapshotVersion: number | null;
  previousSnapshotVersion: number | null;
  finalAnswer: string;
  events: unknown[];
  messageId?: string;
}

export interface GuidelineBuddySessionResult {
  loanId: string;
  vendor: GuidelineBuddyVendor;
  sessionId: string;
  title: string | null;
  status: string;
  lastSnapshotVersionSent: number | null;
  lastSentAt: string | null;
  createdAt: string;
  updatedAt: string;
  metadata: Record<string, unknown> | null;
  history: unknown;
  historyError?: string;
}

export interface GuidelineBuddyPreferences {
  guidelineBuddyVerificationToken?: string | null;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export const sendGuidelineBuddyMessage = async ({
  loanId,
  message,
  mode = 'auto',
  force = false,
}: SendGuidelineBuddyMessageParams): Promise<SendGuidelineBuddyMessageResult> => {
  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<
    ApiEnvelope<SendGuidelineBuddyMessageResult>
  >(`/loans/${loanId}/ai-chat/messages`, {
    method: 'POST',
    body: {
      vendor: GUIDELINE_BUDDY_VENDOR,
      message,
      mode,
      force,
    },
  });

  if (response.data?.data) return response.data.data;
  throw new Error('Failed to send Guideline Buddy message');
};

export const getGuidelineBuddySession = async ({
  loanId,
}: {
  loanId: string;
}): Promise<GuidelineBuddySessionResult | null> => {
  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<
    ApiEnvelope<GuidelineBuddySessionResult | null>
  >(`/loans/${loanId}/ai-chat/session?vendor=${GUIDELINE_BUDDY_VENDOR}`, {
    method: 'GET',
  });

  if (response.data) return response.data.data;
  throw new Error('Failed to load Guideline Buddy session');
};

export const getGuidelineBuddyPreferences =
  async (): Promise<GuidelineBuddyPreferences> => {
    const authTokenHttp = await getCompatibleAuthTokenHttp();
    const response = await authTokenHttp<
      ApiEnvelope<GuidelineBuddyPreferences>
    >('/shared/user/preferences', {
      method: 'GET',
    });

    if (response.data?.data) return response.data.data;
    throw new Error('Failed to load Guideline Buddy preferences');
  };

export const saveGuidelineBuddyVerificationToken = async (
  guidelineBuddyVerificationToken: string | null,
): Promise<GuidelineBuddyPreferences> => {
  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<ApiEnvelope<GuidelineBuddyPreferences>>(
    '/shared/user/preferences',
    {
      method: 'POST',
      body: {
        guidelineBuddyVerificationToken,
      },
    },
  );

  if (response.data?.data) return response.data.data;
  throw new Error('Failed to save Guideline Buddy verification token');
};
